import { useCallback, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import type { StudioDraft } from './studio-draft';
import type { DraftControls } from './StudioSettings';

interface History { past: StudioDraft[]; future: StudioDraft[] }
/** Keep accepted composition edits separate from transient playback writes. */
export function useStudioUndo(storage: DraftControls & { needsReview: boolean; conflict: boolean }) {
  const [history, setHistory] = useState<History>({ past: [], future: [] });
  const latest = useRef(history); latest.current = history;
  const save = (value: History) => { latest.current = value; setHistory(value); };
  const gesture = useRef({ depth: 0, captured: false });
  const gestures = useMemo(() => ({
    begin() { if (gesture.current.depth++ === 0) gesture.current.captured = false; },
    end() { gesture.current.depth = Math.max(0, gesture.current.depth - 1); },
  }), []);
  const update = useCallback<DraftControls['update']>((value, persist = true) => storage.update(current => {
    const next = typeof value === 'function' ? value(current) : value;
    if (persist && next !== current && (!gesture.current.depth || !gesture.current.captured)) {
      save({ past: [...latest.current.past.slice(-39), current], future: [] });
      if (gesture.current.depth) gesture.current.captured = true;
    }
    return next;
  }, persist), [storage.update]);
  const blocked = storage.needsReview || storage.conflict;
  const undo = () => {
    const state = latest.current; if (blocked || !state.past.length) return;
    save({ past: state.past.slice(0, -1), future: [storage.draft, ...state.future] });
    storage.update(state.past.at(-1)!);
  };
  const redo = () => {
    const state = latest.current; if (blocked || !state.future.length) return;
    save({ past: [...state.past, storage.draft], future: state.future.slice(1) });
    storage.update(state.future[0]!);
  };
  const keydown = (event: KeyboardEvent) => {
    if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'z' || (event.target as HTMLElement).closest('input,textarea,select,[contenteditable=true]')) return;
    event.preventDefault(); if (event.shiftKey) redo(); else undo();
  };
  return { update, gestures, undo, redo, keydown, clear: () => save({ past: [], future: [] }), canUndo: !blocked && history.past.length > 0, canRedo: !blocked && history.future.length > 0 };
}
